import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

type ProfileTabPanelProps = {
  title: string;
  icon?: ReactNode;
  description?: ReactNode;
  headerAction?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
};

export function ProfileTabPanel({
  title,
  icon,
  description,
  headerAction,
  children,
  contentClassName,
}: ProfileTabPanelProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="animate-fade-in space-y-4">
        {(description || headerAction) && (
          <div className="flex items-start justify-between gap-3">
            {description ? (
              <p className="min-w-0 flex-1 text-sm leading-relaxed text-gray-light">
                {description}
              </p>
            ) : (
              <span className="sr-only">{title}</span>
            )}
            {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
          </div>
        )}
        <div className={cn("space-y-4", contentClassName)}>{children}</div>
      </div>
    );
  }

  return (
    <Card className="border-primary/20 bg-dark-card">
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center text-white">
              {icon ? <span className="mr-2 inline-flex">{icon}</span> : null}
              {title}
            </CardTitle>
            {description ? (
              <CardDescription>
                <span className="text-gray-light">{description}</span>
              </CardDescription>
            ) : null}
          </div>
          {headerAction}
        </div>
      </CardHeader>
      <CardContent className={contentClassName}>{children}</CardContent>
    </Card>
  );
}

/** Flatten nested cards on mobile (e.g. charts inside workout tab). */
export function profileNestedPanelClass(isMobile: boolean) {
  return cn(
    "border-primary/20 bg-dark-card",
    isMobile && "border-0 bg-transparent shadow-none"
  );
}

export function profileNestedPanelHeaderClass(isMobile: boolean) {
  return cn(isMobile && "px-0 pb-3 pt-1");
}

export function profileNestedPanelContentClass(isMobile: boolean) {
  return cn(isMobile && "px-0 pt-0 pb-1");
}
